import React, { useState } from 'react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { envConfig } from '../config';
import { getAccessToken } from '../auth';
import { popupNotification } from '../popup-notification';
import SmallDialog from '../lib/dialog/SmallDialog';
import { useWorkspaceStore } from '../store/workspace';
import Button from '../lib/button/Button';

interface RequestIntegrationAccessDialogProps {
	integrationKey: string;
	integrationName: string;
	isAlreadyRequested?: boolean;
	onClose: (requested?: boolean) => void;
}

const RequestIntegrationAccessDialog: React.FC<
	RequestIntegrationAccessDialogProps
> = ({ integrationKey, integrationName, isAlreadyRequested, onClose }) => {
	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
	const [reason, setReason] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async () => {
		if (!reason.trim()) {
			popupNotification({
				text: 'Please provide a reason',
				color: 'warning'
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const token = await getAccessToken();
			await axios.post(
				`${envConfig.API_BASE_URL}/support-service/v1/cases`,
				{
					subject: `Access request to: ${integrationName}`,
					body: reason,
					type: 'access-request',
					severity: 'low',
					workspaceId: activeWorkspace?.id,
					metadata: {
						integration: integrationKey
					}
				},
				{
					headers: {
						Authorization: 'Bearer ' + token
					}
				}
			);

			popupNotification({
				text: 'Request submitted successfully!',
				color: 'success'
			});
			onClose(true);
		} catch (error) {
			console.error(error);
			popupNotification({
				text: 'Failed to submit request',
				color: 'error'
			});
			setIsSubmitting(false);
		}
	};

	return (
		<SmallDialog
			style={{
				width: '500px',
				minHeight: 'auto',
				display: 'flex',
				flexDirection: 'column'
			}}
			onClose={() => onClose()}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					alignItems: 'center'
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
					<img
						src={`${envConfig.EXTERNAL_INTEGRATIONS_LOGOS_URL}/${integrationKey}.svg`}
						alt={integrationName}
						style={{ width: 60, height: 60, objectFit: 'contain' }}
					/>
				</div>

				{isAlreadyRequested ? (
					<>
						<div
							style={{
								background: 'rgba(76, 175, 80, 0.08)',
								color: '#4caf50',
								borderRadius: '20px',
								padding: '6px 14px',
								fontSize: '11px',
								fontWeight: 700,
								textTransform: 'uppercase',
								letterSpacing: '1.5px',
								marginBottom: '20px',
								display: 'inline-flex',
								alignItems: 'center',
								gap: '6px',
								border: '1px solid rgba(76, 175, 80, 0.15)'
							}}
						>
							<span
								style={{
									width: '6px',
									height: '6px',
									backgroundColor: '#4caf50',
									borderRadius: '50%',
									display: 'inline-block'
								}}
							/>
							Requested
						</div>
						
						<h2
							style={{
								margin: '0 0 16px 0',
								fontWeight: 500,
								color: 'rgb(0, 0, 0, 0.8)'
							}}
						>
							Request Received
						</h2>
						
						<p
							style={{
								margin: '0 0 24px 0',
								color: 'rgb(0, 0, 0, 0.6)',
								fontSize: '14px',
								lineHeight: '1.5',
								textAlign: 'center'
							}}
						>
							You have already requested access to <strong>{integrationName}</strong>. Our team is reviewing your request.
						</p>
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								width: '100%',
								marginTop: 'auto'
							}}
						>
							<Button onClick={() => onClose()}>
								Close
							</Button>
						</div>
					</>
				) : (
					<>
						<h2
							style={{
								margin: '0 0 16px 0',
								fontWeight: 500,
								color: 'rgb(0, 0, 0, 0.8)'
							}}
						>
							Request Access
						</h2>
						
						<p
							style={{
								margin: '0 0 20px 0',
								color: 'rgb(0, 0, 0, 0.6)',
								fontSize: '14px',
								lineHeight: '1.5',
								textAlign: 'center'
							}}
						>
							The <strong>{integrationName}</strong> integration is
							currently restricted or in development. Please explain why
							you need access to this integration, and our team will
							review your request.
						</p>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								marginBottom: '24px',
								width: '100%'
							}}
						>
							<label
								htmlFor="reason"
								style={{
									marginBottom: '8px',
									fontSize: '13px',
									fontWeight: 500,
									color: 'rgb(0, 0, 0, 0.7)'
								}}
							>
								Reason for access
							</label>
							<textarea
								id="reason"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								placeholder="I need this to sync user data..."
								disabled={isSubmitting}
								rows={4}
								style={{
									resize: 'vertical',
									width: '100%',
									padding: '12px',
									boxSizing: 'border-box',
									borderRadius: '6px',
									border: '1px solid #ccc',
									fontFamily: 'inherit',
									fontSize: '14px'
								}}
							/>
						</div>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								width: '100%',
								marginTop: 'auto'
							}}
						>
							<Button
								onClick={() => onClose()}
								disabled={isSubmitting}
								style={{
									background: 'transparent',
									color: 'rgb(0,0,0,0.7)',
									border: '1px solid #ccc'
								}}
							>
								Cancel
							</Button>
							<Button onClick={handleSubmit} disabled={isSubmitting}>
								{isSubmitting ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									'Submit Request'
								)}
							</Button>
						</div>
					</>
				)}
			</div>
		</SmallDialog>
	);
};

export default RequestIntegrationAccessDialog;
